/**
 * EditCommercialModal (S1.4, S2.2) — the ONE chunked edit for the commercial
 * rows on BOTH mounts of the S2.2 row split (CommercialSalesRows under
 * 銷售與定價, CommercialAccountingRows under 財務資料): base price + currency,
 * item defined cost / qty bounds with their DD!98/100/102 unit qualifiers
 * (S2.2 — selects from the existing `unitsTypeId` family), and costing
 * method (the readiness ref pair with basePrice). Accounting refs stay
 * placeholders (R217) — CLARIFY fields are Blocked and never editable here.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Col, InputNumber, Modal, Popover, Row, Select, Tag, Typography, theme } from 'antd';
import { InfoCircleOutlined, LockOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useFormStyles } from '@/styles/formStyles';
import { controlHeightTokens } from '@/styles/tokens';
import { useConfirmModal } from '@/components/common/ConfirmModal';
import { useErrorHandler } from '@/lib/errors/useErrorHandler';
import { useProductMutations } from '@/hooks/useProducts';
import { pricingAsOf, usePricingDataset } from '@/hooks/usePricing';
import { anchorFigureOf, anchorListPrice, buildCostBreakdown, selectCostRecord } from '@/lib/pricing';
import { effectivePricingAnchor } from '@/lib/pricing/anchor';
import { FeatureGate } from '@/features/flags/FeatureGate';
import { useFeatureFlags } from '@/features/flags/FeatureFlagContext';
import type { Product, ProductCostingMethod, UpdateProductRequest } from '@/types/api/product';
import type { PricingAnchor } from '@/types/api/pricing';
import { UNIT_VALUES } from '@/schemas/forms/productFormSchemas';
import { editCommercialSchema } from '../schemas';
import type { EditCommercialFormData } from '../schemas';
import { isQuietLandedAllocationRow } from '../salesControlsVisibility';

const { Text } = Typography;

/**
 * "!" hover hint (Melissa 2026-08-28: replace visible helper texts with a
 * `!` that opens a Popover with a WHITE content area). Popover — not Tooltip
 * — precisely for that white surface; hover+focus so keyboard users reach it
 * (the antd hover-only default is a recorded ux-review HIGH).
 */
const InfoHint = ({ content, ariaLabel }: { content: ReactNode; ariaLabel: string }) => {
  const { token } = theme.useToken();
  return (
    <Popover
      trigger={['hover', 'focus']}
      content={<div style={{ maxWidth: 320 }}>{content}</div>}
    >
      <span
        tabIndex={0}
        role="img"
        aria-label={ariaLabel}
        style={{ marginInlineStart: 6, color: token.colorTextTertiary, cursor: 'help' }}
      >
        <InfoCircleOutlined />
      </span>
    </Popover>
  );
};

/**
 * The selected method's industry note (board 45:593 — the costing-method
 * table is the cited industrial-method source; the SharePoint workbook is
 * auth-walled and the board table is its published equivalent).
 */
const CostingIndustryNote = ({ method }: { method: ProductCostingMethod }) => {
  const { t } = useTranslation(['products']);
  const { token } = theme.useToken();
  return (
    <div style={{ marginTop: token.marginXS }}>
      <Text strong style={{ fontSize: token.fontSizeSM }}>
        {t('products:detail.costingIndustry.title')}
        {' — '}
        {t(`products:wizard.costingMethods.${method}`)}
      </Text>
      <Text
        type="secondary"
        style={{ display: 'block', fontSize: token.fontSizeSM }}
      >
        {t(`products:detail.costingIndustry.${method}`)}
      </Text>
    </div>
  );
};

/**
 * W6/W5 (V3, flag-gated) — live price & margin trial. EPHEMERAL by design
 * (S0.3 silent-write-loss guard): the three inputs live in local state, are
 * NEVER part of the form payload, and the note says where the real values
 * are maintained (Price Setup › Cost Basis). All math is `buildCostBreakdown`
 * — the one cost waterfall; this component only formats its output.
 */
export const PriceTrialBlock = ({
  productId,
  anchorChoice,
  onAnchorChange,
  costingMethod,
  itemDefinedCost,
  currency,
}: {
  productId: string;
  /** D17 per-product pick — 'follow' = follow the company default. */
  anchorChoice: PricingAnchor | 'follow';
  onAnchorChange: (next: PricingAnchor | 'follow') => void;
  /** The method whose industry note renders below the build-up (locked value or live form pick). */
  costingMethod: ProductCostingMethod | null;
  /**
   * The LIVE Item Defined Cost from the form (Melissa 2026-08-28: it shows
   * first and IS the price the trial computes from — typing a different
   * number moves the whole chain; the cost record only seeds the other
   * inputs, its purchase figure is no longer shown).
   */
  itemDefinedCost: number | null;
  currency: string;
}) => {
  const { t } = useTranslation(['products', 'pricing']);
  const { token } = theme.useToken();
  const { dataset } = usePricingDataset();

  const record = useMemo(
    () =>
      dataset ? selectCostRecord(dataset.costRecords, productId, pricingAsOf()) : null,
    [dataset, productId]
  );

  const [landedAllocation, setLandedAllocation] = useState<number | null>(null);
  const [internalMarkupPct, setInternalMarkupPct] = useState<number | null>(null);
  const [salesMarkupPct, setSalesMarkupPct] = useState<number | null>(null);
  useEffect(() => {
    if (record) {
      setLandedAllocation(record.landedAllocation);
      setInternalMarkupPct(record.internalMarkupPct);
      setSalesMarkupPct(record.salesMarkupPct);
    }
    // Re-seed only when the source record itself changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record?.id]);

  // The ONE cost waterfall (buildCostBreakdown) — anchored on the live
  // Item Defined Cost: 自訂成本 ＋ 分攤 = 到岸 × (1＋內部) = 基準 × (1＋銷售) = 標準售價.
  const breakdown = useMemo(
    () =>
      record && itemDefinedCost != null
        ? buildCostBreakdown({
            ...record,
            purchaseCost: itemDefinedCost,
            landedAllocation: landedAllocation ?? 0,
            internalMarkupPct: internalMarkupPct ?? 0,
            salesMarkupPct: salesMarkupPct ?? 0,
          })
        : null,
    [record, itemDefinedCost, landedAllocation, internalMarkupPct, salesMarkupPct]
  );

  // Melissa 2026-08-28 (2nd pass): the 定價基準 selection DRIVES the trial —
  // only the fields and build-up stages relative to the selected anchor
  // render. Follow = the company default; nothing set anywhere = the full
  // chain from Purchase Cost. Display-only: the resolver and the stored
  // record are untouched.
  //
  // B0 SEAT 4 (2026-08-31) — resolve through the SHARED helper, never off
  // `dataset.pricingAnchorDefault` directly. `PricingSection` renders its
  // anchor chip from `effectivePricingAnchor(product, dataset)`
  // (sections/PricingSection.tsx); this modal used to re-implement the same
  // follow/override precedence inline, so the two could drift and the modal
  // could price off `purchase_cost` while the section rendered a different
  // chip — or none. ONE call site, ONE precedence rule. The LIVE component
  // pick stands in for the stored `product.pricingAnchor` here on purpose:
  // the trial reflects the choice being made, not the one last saved.
  const effective = effectivePricingAnchor(
    { pricingAnchor: anchorChoice === 'follow' ? null : anchorChoice },
    dataset
  );
  // The 'Follow' option's LABEL names the company default specifically, so it
  // asks the same helper the follow question (no product pick) rather than
  // reaching into the dataset field itself. `null` = no company default set.
  const companyAnchorDefault = effectivePricingAnchor({ pricingAnchor: null }, dataset)?.anchor;
  // Neither a product pick nor a company default => the helper returns null;
  // the trial still has to render something, and the full chain from Purchase
  // Cost is the pre-existing fallback (pinned by editCommercialPriceTrial).
  const effectiveAnchor: PricingAnchor = effective?.anchor ?? 'purchase_cost';

  // OPTION B (2026-08-31) — the trial's List Price derives from the EFFECTIVE
  // ANCHOR's figure, through the SAME two domain helpers the engine and the
  // product-page ladder use. No local arithmetic: a third implementation of
  // "which number does the markup multiply" is exactly how the trial and the
  // section came to disagree in the first place.
  const anchorFigure = breakdown ? anchorFigureOf(breakdown, effectiveAnchor) : 0;
  const anchoredListPrice = breakdown ? anchorListPrice(breakdown, effectiveAnchor) : 0;

  // B0 SEAT 5 (2026-08-31) — THE ROW WINDOW, and the genuine bug under it.
  //
  // This used to be ANCHOR_FIRST_ROW: one key per anchor, applied as a START
  // index. That works for `landed_cost` (start at 'landed') and
  // `pricing_base` (start at 'base'), and FAILS for `purchase_cost`, whose
  // first row 'defined_cost' is index 0 — so the filter truncated NOTHING and
  // the panel printed Landed Allocation, Landed Cost and Pricing Base rows
  // driven by two inputs that `showAllocationField` / `showInternalField` had
  // just taken off the screen. A stage row whose inputs are not on screen
  // cannot be read, checked or corrected by the operator looking at it.
  //
  // An explicit per-anchor WINDOW replaces the start index: a row renders
  // only when the anchor's own build-up passes through it.
  //   purchase_cost → the anchor IS the entered cost; nothing is built from
  //                   hidden inputs, so only the anchor row and the list row
  //                   it feeds survive.
  //   landed_cost   → UNCHANGED (landed → base → list). The retained Pricing
  //                   Base row with a blanked ×-markup formula is the
  //                   2026-08-28 hide-paint P0 invariant, ruled d0be609, and
  //                   is deliberately NOT touched by this slice.
  //   pricing_base  → UNCHANGED (base → list).
  //
  // OPTION-B FLIP (2026-08-31) — THE TRUNCATION THAT WAS WAITING ON IT.
  //
  // B0 left `purchase_cost` on the FULL window with a note saying why: while
  // List Price still derived from the pricing base, truncating to
  // ['defined_cost','list'] printed a FALSE equation — 27.50 with
  // "× (1＋25%) Sales Markup" beside AUD 59.25, which is 47.40 × 1.25, not
  // 27.50 × 1.25. Rows fed by off-screen inputs were the lesser evil against
  // arithmetic that does not hold on screen.
  //
  // The arithmetic now follows the anchor (`anchorListPrice` below), so
  // 27.50 × 1.25 = 34.38 IS the answer and the two visible rows compute to
  // each other. The window truncates in the same change that made that true.
  //
  // landed_cost and pricing_base keep their windows. Under `landed_cost` the
  // Pricing Base row is no longer on the sell path — it is kept because it is
  // the quantity the BAND is looked up on (RULING R-BAND), and because
  // blanking its ×-markup formula is the 2026-08-28 hide-paint P0 invariant
  // (ruled d0be609). The `list` row's formula names its own multiplicand, so
  // no reader has to infer which row above it was multiplied.
  const ANCHOR_ROW_WINDOW: Record<PricingAnchor, readonly string[]> = {
    purchase_cost: ['defined_cost', 'list'],
    landed_cost: ['landed', 'base', 'list'],
    pricing_base: ['base', 'list'],
  };
  // CORRECTED 2026-08-31 (Melissa): a field renders when the trial NEEDS it to
  // COMPUTE the selected anchor's figure — not when it sits downstream of it.
  // The previous predicates had purchase_cost and pricing_base swapped.
  //   purchase_cost → the anchor IS the entered cost; nothing to build →
  //                   Sales Markup % only.
  //   landed_cost   → allocation builds landed cost → Allocation + Sales.
  //                   UNCHANGED, and still paints no internal markup anywhere
  //                   (2026-08-28 hide-paint P0 invariant preserved).
  //   pricing_base  → allocation + internal markup build the base →
  //                   Allocation + Internal Markup % + Sales Markup %.
  const showAllocationField = effectiveAnchor !== 'purchase_cost';
  const showInternalField = effectiveAnchor === 'pricing_base';

  const trialField = (
    labelKey: string,
    value: number | null,
    onChange: (next: number | null) => void
  ) => (
    <Col span={8}>
      <Text style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
        {t(labelKey)}
      </Text>
      <InputNumber
        value={value}
        onChange={(next) => onChange(next)}
        size="middle"
        min={0}
        style={{ width: '100%' }}
      />
    </Col>
  );

  return (
    <div
      style={{
        background: token.colorFillQuaternary,
        border: `1px solid ${token.colorBorderSecondary}`,
        borderRadius: token.borderRadiusLG,
        padding: token.paddingMD,
        marginTop: token.marginMD,
      }}
    >
      <Text strong style={{ display: 'block', marginBottom: token.marginXS }}>
        {t('products:detail.trial.title')}
      </Text>
      {/* D17 per-product Pricing Anchor — SELECT (Melissa 2026-08-28 "should
          be selective here"). 'follow' is an explicit option, never a cleared
          void, so the follow-company state stays visible and reachable. Saved
          by the modal's PATCH only when changed.
          The trailing "— config-only either way" is RETRACTED (Option B,
          2026-08-31): saving this pick MOVES this product's ladder prices. The
          band it falls into does not move (R-BAND). */}
      <div style={{ marginBottom: token.marginSM }}>
        <Text style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
          {t('products:detail.anchorSelect.label')}
        </Text>
        <InfoHint
          ariaLabel={t('products:detail.infoAria')}
          content={t('pricing:pricing.productSection.anchorTooltip')}
        />
        <Select
          value={anchorChoice}
          onChange={onAnchorChange}
          size="middle"
          style={{ display: 'block', maxWidth: 360 }}
          options={[
            {
              value: 'follow' as const,
              label: companyAnchorDefault
                ? t('products:detail.anchorSelect.followDefault', {
                    anchor: t(`pricing:pricing.anchor.${companyAnchorDefault}`),
                  })
                : t('products:detail.anchorSelect.followDefaultBare'),
            },
            ...(['purchase_cost', 'landed_cost', 'pricing_base'] as const).map((value) => ({
              value,
              label: t(`pricing:pricing.anchor.${value}`),
            })),
          ]}
        />
      </div>
      {!record || !breakdown ? (
        <span className="tw-inline-flex tw-items-center tw-gap-1">
          <MinusCircleOutlined
            aria-hidden
            style={{ color: token.colorTextTertiary, fontSize: token.sizeXS }}
          />
          <Text type="secondary">
            {!record
              ? t('products:detail.trial.noRecord')
              : t('products:detail.trial.noDefinedCost')}
          </Text>
        </span>
      ) : (
        <>
          <Row gutter={[12, 12]}>
            {showAllocationField &&
              trialField(
                'pricing:pricing.costBasis.fields.landedAllocation',
                landedAllocation,
                setLandedAllocation
              )}
            {showInternalField &&
              trialField(
                'pricing:pricing.costBasis.fields.internalMarkupPct',
                internalMarkupPct,
                setInternalMarkupPct
              )}
            {trialField(
              'pricing:pricing.costBasis.fields.salesMarkupPct',
              salesMarkupPct,
              setSalesMarkupPct
            )}
          </Row>
          {/* Readable build-up (Melissa polish request): stacked stage rows
              in the engine's own vocabulary — mirrors the section's
              waterfall list, replacing the cramped one-line sentence. */}
          <div role="list" style={{ marginTop: token.marginSM }}>
            {[
              // Melissa 2026-08-28: the chain STARTS from the typed Item
              // Defined Cost — no more purchase-document figure in the trial.
              {
                key: 'defined_cost',
                label: t('products:fields.itemDefinedCost'),
                formula: '',
                amount: itemDefinedCost ?? 0,
              },
              {
                key: 'landed_allocation',
                label: t('pricing:pricing.waterfall.landedAllocation'),
                formula: `＋${(landedAllocation ?? 0).toFixed(2)}`,
                amount: landedAllocation ?? 0,
              },
              {
                key: 'landed',
                label: t('pricing:pricing.waterfall.landedCost'),
                formula: `${(itemDefinedCost ?? 0).toFixed(2)}＋${(landedAllocation ?? 0).toFixed(2)}`,
                amount: breakdown.landedCost,
              },
              {
                key: 'base',
                label: t('pricing:pricing.waterfall.pricingBase'),
                // The ×-markup provenance paints only where the internal
                // markup FIELD renders (showInternalField) — a formula that
                // cites a hidden input is unreadable. purchase_cost and
                // landed_cost therefore show the amount alone; landed_cost's
                // 2026-08-28 hide-paint P0 ban is preserved by construction.
                formula: showInternalField
                  ? `× (1＋${internalMarkupPct ?? 0}%) ${t('pricing:pricing.waterfall.internalMarkup')}`
                  : '',
                amount: breakdown.pricingBase,
              },
              {
                key: 'list',
                label: t('pricing:pricing.waterfall.listPrice'),
                // Option B: the formula NAMES its multiplicand. It used to
                // read "× (1＋25%) Sales Markup" with no operand, so a reader
                // had to assume the row directly above was what got
                // multiplied — the assumption that made the pre-flip
                // truncation print a false equation. Now the equation is
                // checkable against the printed number whichever rows the
                // anchor's window happens to render.
                formula: `${anchorFigure.toFixed(2)} × (1＋${salesMarkupPct ?? 0}%) ${t('pricing:pricing.waterfall.salesMarkup')}`,
                amount: anchoredListPrice,
                total: true,
              },
            ]
              // Anchor-relative: only the stages the selected anchor's own
              // build-up passes through render. Earlier stages are not
              // "relative" to it, and later stages fed by inputs the anchor
              // took off the screen are unreadable — both drop out.
              .filter((row) => ANCHOR_ROW_WINDOW[effectiveAnchor].includes(row.key))
              .filter((row) => !isQuietLandedAllocationRow(row))
              .map((row) => (
              <div
                key={row.key}
                role="listitem"
                className="tw-flex tw-items-baseline tw-gap-3"
                style={{
                  padding: `${token.paddingXXS}px 0`,
                  borderBottom: `1px dashed ${token.colorBorderSecondary}`,
                }}
              >
                <span style={{ minWidth: 130 }}>
                  <Text strong={row.total}>{row.label}</Text>
                </span>
                <Text
                  type="secondary"
                  className="tw-flex-1"
                  style={{ fontVariantNumeric: 'tabular-nums', fontSize: token.fontSizeSM }}
                >
                  {row.formula}
                </Text>
                <Text
                  strong
                  style={{
                    fontVariantNumeric: 'tabular-nums',
                    color: row.total ? token.colorPrimary : undefined,
                  }}
                >
                  {row.total ? `${currency} ${row.amount.toFixed(2)}` : row.amount.toFixed(2)}
                </Text>
              </div>
            ))}
          </div>
        </>
      )}
      {/* Melissa 2026-08-28: every costing method's industrial definition
          also shows BELOW the trial — follows the live selection (or the
          locked value), so switching methods visibly changes this line. */}
      {costingMethod && <CostingIndustryNote method={costingMethod} />}
      <Text
        type="secondary"
        style={{ display: 'block', marginTop: token.marginXS, fontSize: token.fontSizeSM }}
      >
        {t('products:detail.trial.note')}
      </Text>
    </div>
  );
};

const CURRENCY_VALUES = ['AUD', 'USD', 'TWD', 'CNY', 'EUR'] as const;
/**
 * The existing `unitsTypeId` family (UT-QTY) is imported, not re-declared —
 * ONE home in @/schemas/forms/productFormSchemas (W7-B3). This file used to
 * carry its own copy, which is exactly how the wizard's list and this modal's
 * list could drift apart without a single gate noticing.
 */
const COSTING_METHOD_VALUES: ProductCostingMethod[] = [
  'FIFO',
  'MOVING_AVERAGE',
  'STANDARD',
  'WEIGHTED_AVERAGE',
];

/**
 * The form spells "no unit" as `null`, the record as `undefined`. Normalize
 * before comparing, or a stored `undefined` reads as dirty against a form
 * `null` and the guard below fires on a no-edit save — the exact failure it
 * exists to stop.
 */
const unitOf = (value: string | null | undefined): string | null => value ?? null;

/**
 * R5/G1 (W-0) — did the operator actually move the ONE unit select this modal
 * renders? The sibling qty units are hydrated into form state but never
 * rendered, so `data.minQtyUnit` / `data.maxQtyUnit` can only ever echo what
 * was loaded; the cost unit is the only unit the operator can touch here.
 */
const isCostUnitDirty = (
  data: EditCommercialFormData,
  product: Pick<Product, 'itemDefinedCostUnit'>
): boolean => unitOf(data.itemDefinedCostUnit) !== unitOf(product.itemDefinedCostUnit);

/** One sibling qty unit this save would rewrite, and the value it would lose. */
export interface UnitOverwrite {
  /** The record field, so the caller resolves its own label. */
  field: 'minQtyUnit' | 'maxQtyUnit';
  /** The STORED value that would be destroyed (null = none stored). */
  from: string | null;
  /** The cost unit that would replace it. */
  to: string | null;
}

/**
 * R11/G2 (W-0) — the sibling qty units a save would REWRITE, named so the
 * confirm can list them. Empty when the cost unit was not moved (R5/G1 omits
 * the keys entirely) and empty when the stored value already equals the new
 * cost unit (an echo destroys nothing). Non-empty ⇒ the operator must consent
 * to losing a value this modal never showed them.
 */
/* eslint-disable-next-line react-refresh/only-export-components -- pure
   predicate beside the modal it serves (BF3 extraction; test seat
   editCommercialUnitOverwriteStop.test.ts). */
export const unitOverwritePreview = (
  data: EditCommercialFormData,
  product: Pick<Product, 'itemDefinedCostUnit' | 'minQtyUnit' | 'maxQtyUnit'>
): UnitOverwrite[] => {
  if (!isCostUnitDirty(data, product)) return [];
  const to = unitOf(data.itemDefinedCostUnit);
  return (
    [
      { field: 'minQtyUnit', from: unitOf(product.minQtyUnit), to },
      { field: 'maxQtyUnit', from: unitOf(product.maxQtyUnit), to },
    ] as const
  ).filter((row): row is UnitOverwrite => row.from !== null && row.from !== row.to);
};

/**
 * R6/G3 (W-0) — do the three STORED units actually agree? `oneUnitHint` says
 * they do; it may only render when that is true. RULING-8 object: the STRING
 * is 林芊's EN full-copy sign-off list and is NOT touched here — only the
 * condition under which it is displayed.
 */
/* eslint-disable-next-line react-refresh/only-export-components -- pure
   predicate beside the modal it serves (BF3 extraction; test seat
   editCommercialUnitOverwriteStop.test.ts). */
export const storedUnitsAgree = (
  product: Pick<Product, 'itemDefinedCostUnit' | 'minQtyUnit' | 'maxQtyUnit'>
): boolean =>
  unitOf(product.minQtyUnit) === unitOf(product.itemDefinedCostUnit) &&
  unitOf(product.maxQtyUnit) === unitOf(product.itemDefinedCostUnit);

/**
 * The modal's PATCH body as a pure function (BF3 pure-predicate extraction) —
 * the omission doctrine lives here so a test can reach it without a render.
 */
/* eslint-disable-next-line react-refresh/only-export-components -- the PATCH
   builder must live beside the modal it serves (BF3 pure-predicate
   extraction; test seat editCommercialCurrencySystemSet.test.ts) — the
   CommercialSection routing-data precedent. */
export const buildCommercialPatch = (
  data: EditCommercialFormData,
  product: Pick<Product, 'costingMethodLocked' | 'itemDefinedCostUnit'>,
  anchor: { dirty: boolean; choice: PricingAnchor | 'follow' },
  currencySystemSet: boolean
): UpdateProductRequest => ({
  // Locked ⇒ omit entirely: never echo even the unchanged value, so a
  // strict N9 server predicate can reject ANY write to the field.
  ...(product.costingMethodLocked ? {} : { costingMethod: data.costingMethod }),
  // D17 — null clears back to follow the company default.
  ...(anchor.dirty ? { pricingAnchor: anchor.choice === 'follow' ? null : anchor.choice } : {}),
  // Base Price is NOT manually overwritable (Melissa 2026-08-28) — the
  // engine's List Price is the standard sale price; never PATCHed here.
  // RULING-2: system-set ⇒ omit entirely (never echo even the unchanged
  // value, so a strict N9 server predicate can reject ANY write) — the
  // same doctrine as the costingMethod lock above.
  ...(currencySystemSet ? {} : { currency: data.currency }),
  // Null-clear (not omit): a cleared amount must PATCH so the mock
  // drops the previous value. 0 stays 0 (list then hides the pair).
  minQty: data.minQty ?? null,
  maxQty: data.maxQty ?? null,
  itemDefinedCost: data.itemDefinedCost ?? null,
  // R5/G1 (W-0, INTERIM — Q15/C-X1 is OPEN): this modal renders ONE unit
  // select and never shows minQtyUnit / maxQtyUnit, so it must not write
  // them unless the operator actually moved the cost unit. Omission (not
  // `null`) is the house idiom — the apply seat keeps any omitted key
  // (packages/domain/src/rules/product.ts:299,301), the same doctrine as
  // the costingMethod lock and the currency system-set omission above.
  // The previous unconditional mirror DESTROYED stored qty units on a
  // no-edit save (measured: 5 of 9 seeded products lost a value, 2 gained
  // orphans). Correct under BOTH branches of Q15: ruling A (one unit)
  // replaces this with an explicit disclosed mirror, ruling B (two
  // families) replaces it with two real selects. It settles neither.
  ...(isCostUnitDirty(data, product)
    ? {
        minQtyUnit: data.itemDefinedCostUnit ?? null,
        maxQtyUnit: data.itemDefinedCostUnit ?? null,
      }
    : {}),
  itemDefinedCostUnit: data.itemDefinedCostUnit ?? null,
});

interface EditCommercialModalProps {
  open: boolean;
  product: Product;
  onCancel: () => void;
  onSaved: () => void;
}

export const EditCommercialModal = ({
  open,
  product,
  onCancel,
  onSaved,
}: EditCommercialModalProps) => {
  const { t } = useTranslation(['products', 'common']);
  const { token } = theme.useToken();
  const formStyles = useFormStyles();
  const { showConfirm } = useConfirmModal();
  const { handleError, handleSuccess } = useErrorHandler();
  const { updateProduct } = useProductMutations(product.id);
  const { isEnabled } = useFeatureFlags();
  const pricingOn = isEnabled('engines.pricing');
  // Policy lock (RULING-2 2026-08-29), flag-gated because — unlike
  // `costingMethodLocked`, which mirrors an honest server field — no server
  // field exists yet; flag-off production keeps the editable Select
  // byte-identically. Real refusal predicate = N9 (Joe/Finance trigger).
  const currencySystemSet = pricingOn;

  const formValues = useMemo<EditCommercialFormData>(
    () => ({
      costingMethod: product.costingMethod ?? null,
      basePrice: product.basePrice ?? null,
      currency: product.currency ?? 'AUD',
      minQty: product.minQty ?? null,
      minQtyUnit: product.minQtyUnit ?? null,
      maxQty: product.maxQty ?? null,
      maxQtyUnit: product.maxQtyUnit ?? null,
      itemDefinedCost: product.itemDefinedCost ?? null,
      itemDefinedCostUnit: product.itemDefinedCostUnit ?? null,
    }),
    [product]
  );

  const {
    control,
    handleSubmit,
    reset,
    trigger,
    formState: { errors, isSubmitting },
  } = useForm<EditCommercialFormData>({
    resolver: zodResolver(editCommercialSchema),
    defaultValues: formValues,
    values: formValues,
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  // D17 per-product anchor — COMPONENT state, deliberately outside the zod
  // schema (the field registry stays frozen; the anchor is config, not a
  // registry field). 'follow' = follow the company default.
  const [anchorChoice, setAnchorChoice] = useState<PricingAnchor | 'follow'>(
    product.pricingAnchor ?? 'follow'
  );

  useEffect(() => {
    if (open) {
      reset(formValues);
      setAnchorChoice(product.pricingAnchor ?? 'follow');
    }
  }, [open, formValues, reset, product.pricingAnchor]);

  // The industry note follows the LIVE pick; the locked value wins when locked.
  const watchedCostingMethod = useWatch({ control, name: 'costingMethod' });
  const industryMethod = product.costingMethodLocked
    ? (product.costingMethod ?? null)
    : (watchedCostingMethod ?? null);

  // Melissa 2026-08-28: the trial computes LIVE from the typed Item Defined
  // Cost — the form field above is the one source; typing moves the chain.
  const watchedItemDefinedCost = useWatch({ control, name: 'itemDefinedCost' });
  const watchedCurrency = useWatch({ control, name: 'currency' });

  // Dirty check doubles as the flag gate: at flag-off the select never
  // renders, so the choice can never change and the payload stays untouched.
  const anchorDirty = anchorChoice !== (product.pricingAnchor ?? 'follow');

  // R6/G3 (W-0) — `oneUnitHint` asserts one-unit semantics. It may only
  // render when that assertion is TRUE of the stored record; it was measured
  // on screen while the store held SET / BOX / CTN. RULING-8 object (Melissa
  // ruled on this STRING 2026-08-29): the string is unchanged — 林芊's EN
  // full-copy sign-off list owns it — only the condition is new.
  const oneUnitHintIsTrue = storedUnitsAgree(product);

  /**
   * R11/G2 (W-0) — explicit consent before the cost unit rewrites sibling qty
   * units the operator cannot see. The confirm NAMES each field and its
   * before → after value; a hover-only "!" is not consent (`oneUnitHint`
   * existed and prevented none of the measured losses). House pattern: the SO
   * re-resolve confirm (`SoLineEditor.tsx` — modal names the value it
   * replaces).
   */
  const confirmUnitOverwrite = (overwrites: UnitOverwrite[]): Promise<boolean> =>
    new Promise<boolean>((resolve) => {
      showConfirm({
        title: t('products:detail.unitOverwrite.confirmTitle'),
        content: t('products:detail.unitOverwrite.confirmBody', {
          changes: overwrites
            .map((row) => `${t(`products:fields.${row.field}`)} ${row.from} → ${row.to ?? '—'}`)
            .join('; '),
        }),
        okText: t('common:actions.save'),
        cancelText: t('common:actions.cancel'),
        danger: false,
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });

  const onSubmit = async (data: EditCommercialFormData) => {
    try {
      const overwrites = unitOverwritePreview(data, product);
      if (overwrites.length > 0 && !(await confirmUnitOverwrite(overwrites))) return;
      await updateProduct(
        buildCommercialPatch(
          data,
          product,
          { dirty: anchorDirty, choice: anchorChoice },
          currencySystemSet
        )
      );
      handleSuccess(t('products:messages.updated'));
      onSaved();
      onCancel();
    } catch (error) {
      handleError(error);
    }
  };

  // S2.2 — DD!98/100/102 unit qualifiers, selects from the wizard's unit family.
  const unitField = (
    name: 'itemDefinedCostUnit' | 'minQtyUnit' | 'maxQtyUnit',
    labelKey: string
  ) => (
    <Col span={12}>
      <Text style={formStyles.labelStyle}>
        {t(labelKey)}
        {/* W7 one-unit ruling, now as a "!" hint instead of a visible line.
            R6/G3: shown ONLY while the stored units actually agree — the
            sentence is an assertion, not a policy banner. String unchanged
            (RULING-8 object; 林芊's EN sign-off list owns the wording). */}
        {name === 'itemDefinedCostUnit' && oneUnitHintIsTrue && (
          <InfoHint
            ariaLabel={t('products:detail.infoAria')}
            content={t('products:detail.oneUnitHint')}
          />
        )}
      </Text>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <Select
            value={field.value ?? undefined}
            onChange={(value) => field.onChange(value ?? null)}
            allowClear
            size="large"
            options={UNIT_VALUES.map((unit) => ({ value: unit, label: unit }))}
            style={formStyles.inputStyle}
          />
        )}
      />
    </Col>
  );

  const numberField = (
    name: 'minQty' | 'maxQty' | 'itemDefinedCost',
    labelKey: string
  ) => (
    <Col span={12}>
      <Text style={formStyles.labelStyle}>
        {t(labelKey)}
        {/* Very-short PM-word "!" popover (Melissa 2026-08-28). */}
        {name === 'itemDefinedCost' && (
          <InfoHint
            ariaLabel={t('products:detail.infoAria')}
            content={t('products:detail.fieldInfo.itemDefinedCost')}
          />
        )}
      </Text>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <>
            <InputNumber
              value={field.value ?? null}
              onChange={(value) => {
                field.onChange(value);
                trigger(name);
              }}
              onBlur={field.onBlur}
              size="large"
              min={0}
              status={errors[name] ? 'error' : undefined}
              style={formStyles.inputStyle}
            />
            {errors[name] && (
              <Text style={formStyles.errorStyle}>{t(errors[name]?.message ?? '')}</Text>
            )}
          </>
        )}
      />
    </Col>
  );

  return (
    <Modal
      // PROPOSED (RULING-3 2026-08-29) — pending 林芊 EN sign-off + Melissa
      // mock walk: flag-picked dual keys; flag-off keeps the legacy title.
      title={t(
        pricingOn ? 'products:detail.modals.editCommercialEngine' : 'products:detail.modals.editCommercial'
      )}
      open={open}
      onCancel={onCancel}
      onOk={handleSubmit(onSubmit)}
      okText={t('common:actions.save')}
      cancelText={t('common:actions.cancel')}
      confirmLoading={isSubmitting}
      width={800}
      okButtonProps={{ style: { height: `${controlHeightTokens.controlHeightXL}px` } }}
      cancelButtonProps={{ style: { height: `${controlHeightTokens.controlHeightXL}px` } }}
    >
      <form>
        <div style={formStyles.fieldContainerStyle}>
          <Text strong style={formStyles.sectionTitleStyle}>
            {/* PROPOSED (RULING-3 2026-08-29) — pending 林芊 EN sign-off +
                Melissa mock walk: dual keys, legacy value at flag-off. */}
            {t(
              pricingOn ? 'products:wizard.sections.pricingCost' : 'products:wizard.sections.commercial'
            )}
          </Text>
          {/* Melissa 2026-08-28: Item Defined Cost FIRST — it is the price
              the trial computes from. Base Price is NOT manually
              overwritable: read-only derived display, no input. */}
          <Row gutter={[16, 16]}>
            {numberField('itemDefinedCost', 'products:fields.itemDefinedCost')}
            {/* W7 one-unit ruling: ONE unit select — qty bounds follow it. */}
            {unitField('itemDefinedCostUnit', 'products:fields.itemDefinedCostUnit')}
            <Col span={12}>
              <Text style={formStyles.labelStyle}>
                {t('products:fields.currency')}
                {currencySystemSet && (
                  <InfoHint
                    ariaLabel={t('products:detail.infoAria')}
                    content={t('products:detail.currencySystemSet.reason')}
                  />
                )}
              </Text>
              {currencySystemSet ? (
                /* RULING-2 (2026-08-29) W6 lock display: STATE at rest —
                   read-only text + lock icon + System-set tag; the REASON
                   lives in the "!" popover above (white content area) and
                   stays announced via aria-describedby. Never a disabled
                   Select. Flag-gated (unlike costingMethodLocked there is
                   no server field — this is policy); flag-off keeps the
                   editable Select byte-identically. Inline duplication of
                   the costingMethod box is deliberate — the two locked
                   boxes stay independently editable (the holistic net-new
                   shared-component set is closed). */
                <div
                  tabIndex={0}
                  aria-describedby="currency-system-set-reason"
                  style={{
                    ...formStyles.inputStyle,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    minHeight: `${controlHeightTokens.controlHeightXL}px`,
                    background: token.colorFillQuaternary,
                    border: `1px dashed ${token.colorBorder}`,
                    borderRadius: token.borderRadiusLG,
                    paddingInline: token.paddingSM,
                  }}
                >
                  <Text>{product.currency ?? 'AUD'}</Text>
                  <Tag icon={<LockOutlined />} style={{ margin: 0 }}>
                    {t('products:detail.currencySystemSet.tag')}
                  </Tag>
                  <span id="currency-system-set-reason" style={{ display: 'none' }}>
                    {t('products:detail.currencySystemSet.reason')}
                  </span>
                </div>
              ) : (
                <Controller
                  name="currency"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      size="large"
                      options={CURRENCY_VALUES.map((code) => ({ value: code, label: code }))}
                      style={formStyles.inputStyle}
                    />
                  )}
                />
              )}
            </Col>
            <Col span={12}>
              <Text style={formStyles.labelStyle}>
                {t('products:fields.basePrice')}
                <InfoHint
                  ariaLabel={t('products:detail.infoAria')}
                  content={t('products:detail.fieldInfo.basePrice')}
                />
              </Text>
              <div
                style={{
                  ...formStyles.inputStyle,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  minHeight: `${controlHeightTokens.controlHeightXL}px`,
                  background: token.colorFillQuaternary,
                  border: `1px dashed ${token.colorBorder}`,
                  borderRadius: token.borderRadiusLG,
                  paddingInline: token.paddingSM,
                }}
              >
                <Text style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {product.basePrice != null
                    ? `${product.currency ?? ''} ${product.basePrice.toFixed(2)}`.trim()
                    : '—'}
                </Text>
                {/* NOT "Derived" — this is the STORED manual scalar
                    (Product.basePrice), and on seeded data it reads 89.00
                    while the engine's own List Price below reads 150.00.
                    Tagging a stored number as derived put two different
                    標準售價 on one screen with one of them lying. It is
                    read-only HERE (Melissa: don't allow overwriting it), and
                    the tag now says where it is maintained instead. */}
                <Tag style={{ margin: 0 }}>{t('products:detail.basePriceStored')}</Tag>
              </div>
            </Col>
            {numberField('minQty', 'products:fields.minQty')}
            {numberField('maxQty', 'products:fields.maxQty')}
            <Col span={12}>
              <Text style={formStyles.labelStyle}>
                {t('products:fields.costingMethod')}
                {/* "!" white popover (Melissa 2026-08-28): engine note + the
                    selected method's industry definition — replaces the old
                    always-visible helper line. */}
                <InfoHint
                  ariaLabel={t('products:detail.infoAria')}
                  content={
                    <>
                      <Text style={{ display: 'block' }}>
                        {t('products:detail.costingMethodEngineNote')}
                      </Text>
                      {product.costingMethodLocked && (
                        <Text style={{ display: 'block', marginTop: 8 }}>
                          {t('products:detail.costingLocked.reason')}
                        </Text>
                      )}
                      {industryMethod && <CostingIndustryNote method={industryMethod} />}
                    </>
                  }
                />
              </Text>
              {product.costingMethodLocked && product.costingMethod ? (
                /* W6 lock display (delta-4): STATE at rest — read-only text +
                   lock icon + Locked tag; the REASON now lives in the "!"
                   popover above (white content area) and stays announced via
                   aria-describedby. Never a disabled Select. Mock predicate;
                   real predicate = N9 (Joe). DELIBERATELY outside the
                   engines.pricing gate: the lock mirrors honest SERVER state
                   (the field), not engine behaviour — absent ⇒ editable
                   exactly as before, so flag-off production is unchanged
                   until N9 ships the field. */
                <div
                  tabIndex={0}
                  aria-describedby="costing-locked-reason"
                  style={{
                    ...formStyles.inputStyle,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    minHeight: `${controlHeightTokens.controlHeightXL}px`,
                    background: token.colorFillQuaternary,
                    border: `1px dashed ${token.colorBorder}`,
                    borderRadius: token.borderRadiusLG,
                    paddingInline: token.paddingSM,
                  }}
                >
                  <Text>{t(`products:wizard.costingMethods.${product.costingMethod}`)}</Text>
                  <Tag icon={<LockOutlined />} style={{ margin: 0 }}>
                    {t('products:detail.costingLocked.tag')}
                  </Tag>
                  <span id="costing-locked-reason" style={{ display: 'none' }}>
                    {t('products:detail.costingLocked.reason')}
                  </span>
                </div>
              ) : (
                <Controller
                  name="costingMethod"
                  control={control}
                  render={({ field }) => (
                    <Select
                      value={field.value ?? undefined}
                      onChange={(value) => field.onChange(value ?? null)}
                      allowClear
                      size="large"
                      options={COSTING_METHOD_VALUES.map((method) => ({
                        value: method,
                        label: t(`products:wizard.costingMethods.${method}`),
                      }))}
                      style={formStyles.inputStyle}
                    />
                  )}
                />
              )}
            </Col>
          </Row>

          {/* V3 (flag-gated): ephemeral price & margin trial + anchor chip —
              renders nothing at flag-off, so production keeps today's modal. */}
          <FeatureGate flag="engines.pricing" showDisabled={false}>
            <PriceTrialBlock
              productId={product.id}
              anchorChoice={anchorChoice}
              onAnchorChange={setAnchorChoice}
              costingMethod={industryMethod}
              itemDefinedCost={watchedItemDefinedCost ?? null}
              currency={watchedCurrency ?? 'AUD'}
            />
          </FeatureGate>
          <Text
            type="secondary"
            style={{ fontSize: token.fontSizeSM, display: 'block', marginTop: '8px' }}
          >
            {t('products:detail.commercialReadinessHint')}
          </Text>
        </div>
      </form>
    </Modal>
  );
};

export default EditCommercialModal;
